/**
 * CutsceneEditor - Main editor route for creating and editing cutscenes
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  CutsceneData,
  CutsceneActor,
  createEmptyCutscene,
} from "@/lib/cutscene/types";
import {
  downloadCutsceneJSON,
  importCutsceneFromFile,
  formatDuration,
  calculateCutsceneDuration,
} from "@/lib/cutscene/utils";
import {
  VideoRecorder,
  VideoRecorderState,
} from "@/lib/cutscene/VideoRecorder";
import { PreviewCanvas } from "@/components/cutscene/PreviewCanvas";
import { EventTimeline } from "@/components/cutscene/EventTimeline";
import { EventPropertyPanel } from "@/components/cutscene/EventPropertyPanel";
import { AssetBrowser } from "@/components/cutscene/AssetBrowser";
import { DialogBox } from "@/components/cutscene/DialogBox";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";

type AssetBrowserType = "map" | "vfx" | "pokemon" | null;

const CutsceneEditor: React.FC = () => {
  // Cutscene state
  const [cutscene, setCutscene] = useState<CutsceneData>(createEmptyCutscene());
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [actors, setActors] = useState<Record<string, CutsceneActor>>({});

  // Dialog state
  const [dialogState, setDialogState] = useState({
    visible: false,
    speaker: "",
    portrait: undefined as string | undefined,
    text: "",
  });

  // Fade state
  const [fadeState, setFadeState] = useState({
    visible: false,
    opacity: 0,
    color: "#000000",
  });

  // UI state
  const [assetBrowserType, setAssetBrowserType] =
    useState<AssetBrowserType>(null);
  const [showGrid, setShowGrid] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Video recording state
  const videoRecorderRef = useRef<VideoRecorder | null>(null);
  const [recordingState, setRecordingState] = useState<VideoRecorderState>({
    isRecording: false,
    isPaused: false,
    duration: 0,
    blob: null,
  });

  // Initialize video recorder
  useEffect(() => {
    videoRecorderRef.current = new VideoRecorder();
    videoRecorderRef.current.setOnStateChange(setRecordingState);

    return () => {
      videoRecorderRef.current = null;
    };
  }, []);

  // Handlers
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleStop = useCallback(() => {
    setIsPlaying(false);
    setActors({});
    setDialogState({
      visible: false,
      speaker: "",
      portrait: undefined,
      text: "",
    });
    setFadeState({
      visible: false,
      opacity: 0,
      color: "#000000",
    });

    // Stop recording if active
    if (recordingState.isRecording) {
      videoRecorderRef.current?.stop();
    }
  }, [recordingState.isRecording]);

  const handleRecord = useCallback(async () => {
    if (!videoRecorderRef.current) return;

    if (recordingState.isRecording) {
      // Stop recording
      const blob = await videoRecorderRef.current.stop();
      if (blob) {
        videoRecorderRef.current.download(cutscene.name || "cutscene");
      }
    } else {
      // Find canvas element and start recording
      const canvas = document.querySelector(
        "#game-container canvas",
      ) as HTMLCanvasElement;
      if (!canvas) {
        alert("Canvas not found. Please wait for the preview to load.");
        return;
      }

      videoRecorderRef.current.setCanvas(canvas);
      const started = videoRecorderRef.current.start();

      if (started) {
        // Auto-play cutscene when recording starts
        setIsPlaying(true);
      } else {
        alert(
          "Failed to start recording. Your browser may not support video recording.",
        );
      }
    }
  }, [recordingState.isRecording, cutscene.name]);

  const handleExport = useCallback(() => {
    downloadCutsceneJSON(cutscene);
  }, [cutscene]);

  const handleImport = useCallback(async () => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const imported = await importCutsceneFromFile(file);
        setCutscene(imported);
        setSelectedEventId(null);
        setIsPlaying(false);
      } catch (error) {
        console.error("Failed to import cutscene:", error);
        alert("Failed to import cutscene. Check console for details.");
      }

      // Reset input
      e.target.value = "";
    },
    [],
  );

  const handleNewCutscene = useCallback(() => {
    if (
      cutscene.events.length > 0 &&
      !confirm("Create new cutscene? Unsaved changes will be lost.")
    ) {
      return;
    }
    setCutscene(createEmptyCutscene());
    setSelectedEventId(null);
    setIsPlaying(false);
    setActors({});
  }, [cutscene.events.length]);

  const handleOpenAssetBrowser = useCallback(
    (type: "map" | "vfx" | "pokemon") => {
      setAssetBrowserType(type);
    },
    [],
  );

  const handleAssetSelect = useCallback(
    (value: string) => {
      if (assetBrowserType === "map") {
        setCutscene((prev) => ({ ...prev, mapId: value }));
      } else if (assetBrowserType === "pokemon" && selectedEventId) {
        const eventIndex = cutscene.events.findIndex(
          (e) => e.id === selectedEventId,
        );
        if (eventIndex >= 0) {
          const event = cutscene.events[eventIndex];
          if (event.type === "spawn_pokemon") {
            const newEvents = [...cutscene.events];
            newEvents[eventIndex] = { ...event, pokemonName: value };
            setCutscene((prev) => ({ ...prev, events: newEvents }));
          }
        }
      } else if (assetBrowserType === "vfx" && selectedEventId) {
        const eventIndex = cutscene.events.findIndex(
          (e) => e.id === selectedEventId,
        );
        if (eventIndex >= 0) {
          const event = cutscene.events[eventIndex];
          if (event.type === "play_vfx") {
            const newEvents = [...cutscene.events];
            newEvents[eventIndex] = { ...event, spritePath: value };
            setCutscene((prev) => ({ ...prev, events: newEvents }));
          }
        }
      }
      setAssetBrowserType(null);
    },
    [assetBrowserType, cutscene.events, selectedEventId],
  );

  const handleCutsceneNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCutscene((prev) => ({ ...prev, name: e.target.value }));
    },
    [],
  );

  const totalDuration = calculateCutsceneDuration(cutscene);

  return (
    <div className="flex flex-col h-screen bg-[#0f1219] text-white">
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#161b26] border-b border-gray-700">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold text-blue-400">Cutscene Editor</h1>
          <input
            type="text"
            value={cutscene.name}
            onChange={handleCutsceneNameChange}
            className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-600 rounded focus:border-blue-500 focus:outline-none"
            placeholder="Cutscene Name"
          />
          <span className="text-sm text-gray-500">
            ~{formatDuration(totalDuration)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Playback Controls */}
          {!isPlaying ? (
            <button
              onClick={handlePlay}
              disabled={
                cutscene.events.length === 0 || recordingState.isRecording
              }
              className="px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-md flex items-center gap-2"
            >
              <span>▶</span> Play
            </button>
          ) : (
            <button
              onClick={handleStop}
              disabled={recordingState.isRecording}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-gray-600 rounded-md flex items-center gap-2"
            >
              <span>■</span> Stop
            </button>
          )}

          {/* Record Button */}
          <button
            onClick={handleRecord}
            disabled={cutscene.events.length === 0}
            className={`px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 ${
              recordingState.isRecording
                ? "bg-red-600 hover:bg-red-700 animate-pulse"
                : "bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
            }`}
            title={
              recordingState.isRecording
                ? "Stop Recording & Download"
                : "Record Video"
            }
          >
            <span className={recordingState.isRecording ? "text-white" : ""}>
              {recordingState.isRecording ? "⏺" : "🎬"}
            </span>
            {recordingState.isRecording
              ? `Recording... ${formatDuration(recordingState.duration)}`
              : "Record"}
          </button>

          <div className="w-px h-6 bg-gray-600 mx-2" />

          {/* Grid Toggle */}
          <button
            onClick={() => setShowGrid(!showGrid)}
            className={`px-3 py-2 text-sm rounded-md ${
              showGrid ? "bg-blue-600" : "bg-gray-700 hover:bg-gray-600"
            }`}
            title="Toggle Grid"
          >
            ⊞
          </button>

          {/* Map Selector */}
          <button
            onClick={() => setAssetBrowserType("map")}
            className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-md"
            title="Select Map"
          >
            🗺 {cutscene.mapId}
          </button>

          <div className="w-px h-6 bg-gray-600 mx-2" />

          {/* File Operations */}
          <button
            onClick={handleNewCutscene}
            className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-md"
            title="New Cutscene"
          >
            📄 New
          </button>
          <button
            onClick={handleImport}
            className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-md"
            title="Import JSON"
          >
            📁 Import
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 rounded-md"
            title="Export JSON"
          >
            💾 Export
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Timeline */}
        <div className="w-80 flex-shrink-0">
          <EventTimeline
            cutscene={cutscene}
            onCutsceneChange={setCutscene}
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
            currentEventIndex={currentEventIndex}
            isPlaying={isPlaying}
          />
        </div>

        {/* Center - Preview Canvas */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 relative">
            <PreviewCanvas
              cutscene={cutscene}
              isPlaying={isPlaying}
              onPlayStateChange={setIsPlaying}
              onEventIndexChange={setCurrentEventIndex}
              currentEventIndex={currentEventIndex}
              onActorsChange={setActors}
              showGrid={showGrid}
              dialogState={dialogState}
              onDialogStateChange={setDialogState}
              fadeState={fadeState}
              onFadeStateChange={setFadeState}
            />

            {/* Pokemon Sprites Overlay (when playing) */}
            {isPlaying && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {Object.values(actors).map((actor) => (
                  <div
                    key={actor.id}
                    className="absolute"
                    style={{
                      left: `${(actor.position.x / 400) * 100}%`,
                      top: `${(actor.position.y / 750) * 100}%`,
                      transform: `translate(-50%, -50%) scale(${actor.scale})`,
                      opacity: actor.visible ? 1 : 0,
                      transition: "opacity 0.3s",
                    }}
                  >
                    <PokemonSprite
                      pokemon={{ base_pokemon_name: actor.pokemonName }}
                      animation={actor.animation}
                      direction={actor.direction}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Dialog Box Overlay */}
            <DialogBox
              visible={dialogState.visible}
              speaker={dialogState.speaker}
              portrait={dialogState.portrait}
              text={dialogState.text}
            />

            {/* Fade Overlay */}
            {fadeState.visible && (
              <div
                className="absolute inset-0 z-[200] pointer-events-none"
                style={{
                  backgroundColor: fadeState.color,
                  opacity: fadeState.opacity,
                }}
              />
            )}
          </div>

          {/* Bottom Panel - Properties */}
          <div className="h-64 flex-shrink-0">
            <EventPropertyPanel
              cutscene={cutscene}
              onCutsceneChange={setCutscene}
              selectedEventId={selectedEventId}
              actors={actors}
              onOpenAssetBrowser={handleOpenAssetBrowser}
            />
          </div>
        </div>
      </div>

      {/* Asset Browser Modal */}
      <AssetBrowser
        isOpen={assetBrowserType !== null}
        onClose={() => setAssetBrowserType(null)}
        type={assetBrowserType || "map"}
        onSelect={handleAssetSelect}
        currentValue={assetBrowserType === "map" ? cutscene.mapId : undefined}
      />

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default CutsceneEditor;
